import axios from "axios";
import tokenService from "../auth/tokenService";
import axiosRefresh from "./axiosRefresh";

const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 15000,
    headers: { "Content-Type": "application/json" },
});

/* ── REQUEST: Attach access token to every call ─────────────── */
axiosInstance.interceptors.request.use(
    (config) => {
        const token = tokenService.getAccessToken();
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

/* ── RESPONSE: Catch 401 → refresh → retry ──────────────────── */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
    failedQueue = [];
};

axiosInstance.interceptors.response.use(
    (res) => res,

    async (error) => {
        const original = error.config;

        if (error.response?.status === 401 && !original._retry) {

            if (isRefreshing) {
                // Queue concurrent requests while refresh is happening
                return new Promise((resolve, reject) =>
                    failedQueue.push({ resolve, reject })
                ).then((token) => {
                    original.headers["Authorization"] = `Bearer ${token}`;
                    return axiosInstance(original);
                });
            }

            original._retry = true;
            isRefreshing = true;

            const refreshToken = tokenService.getRefreshToken();

            if (!refreshToken) {
                tokenService.clearAll();
                window.location.href = "/login";
                return Promise.reject(error);
            }

            try {
                const { data } = await axiosRefresh.post("/api/auth/refresh/", {
                    refresh: refreshToken,
                });

                const newToken = data.access;
                tokenService.setAccessToken(newToken);
                if (data.refresh) {
                    tokenService.setRefreshToken(data.refresh);
                }
                processQueue(null, newToken);

                original.headers["Authorization"] = `Bearer ${newToken}`;
                return axiosInstance(original);

            } catch (refreshError) {
                processQueue(refreshError, null);
                tokenService.clearAll();
                window.location.href = "/login";
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;